import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

// Usage: data-controller="avatar-upload"
// Drives the avatar selection + in-browser crop flow on the settings form.
export default class extends Controller {
  static targets = [
    "fileInput",
    "preview",
    "initials",
    "removeFlag",
    "removeBtn",
    "photoBlock",
    "existingActions",
    "addPhotoBtn",
    "modal",
    "uploadModal",
    "uploadModalContent",
    "uploadPreview",
    "uploadPreviewImage",
    "uploadPreviewMeta",
    "uploadActions",
    "dropzone",
    "removeModal",
    "removePreviewImage",
    "removePreviewMeta",
    "cropperImage",
    "confirmBtn",
    "successToast",
    "errorToast"
  ]
  static values = {
    initials: String,
    outputSize: { type: Number, default: 512 },
    hasAvatar: Boolean,
    existingAvatarUrl: String,
    existingAvatarName: String,
    existingAvatarSize: Number,
    uploadUrl: String,
    removeUrl: String
  }

  connect() {
    if (this.hasModalTarget) {
      this._cropModal = new Modal(this.modalTarget)
      this._onCropModalHidden = this._teardownCropper.bind(this)
      this.modalTarget.addEventListener("hidden.bs.modal", this._onCropModalHidden)
    }

    if (this.hasUploadModalTarget) {
      this._uploadModal = new Modal(this.uploadModalTarget)
    }

    if (this.hasRemoveModalTarget) {
      this._removeModal = new Modal(this.removeModalTarget)
    }
  }

  disconnect() {
    if (this.hasModalTarget) {
      this.modalTarget.removeEventListener("hidden.bs.modal", this._onCropModalHidden)
    }

    this._teardownCropper()
    this._revokePendingPreviewUrl()
    this._cropModal?.dispose()
    this._uploadModal?.dispose()
    this._removeModal?.dispose()
  }

  openUploadModal(event) {
    event?.preventDefault()

    if (this.hasUploadModalTarget) {
      this._uploadConfirmed = false

      if (this._pendingPreviewUrl) {
        const file = this.fileInputTarget.files?.[0]
        if (file) {
          this.uploadPreviewImageTarget.src = this._pendingPreviewUrl
          this.uploadPreviewMetaTarget.textContent = `${file.name} · ${this._formatFileSize(file.size)}`
        }
        this._showUploadPreviewState()
      } else if (this.hasAvatarValue) {
        this._showExistingAvatarInModal()
      } else {
        this._showUploadEmptyState()
      }

      this._uploadModal.show()
      return
    }

    this.openFilePicker(event)
  }

  openFilePicker(event) {
    event?.preventDefault()
    this.fileInputTarget.click()
  }

  closeUploadModal(event) {
    event?.preventDefault()

    if (!this._uploadConfirmed && this._pendingPreviewUrl) {
      this._resetFileInput()
      this._revokePendingPreviewUrl()
    }

    this._showUploadEmptyState()
    this._uploadModal?.hide()
  }

  async confirmUpload(event) {
    event?.preventDefault()

    const file = this.fileInputTarget.files?.[0]

    if (!file) {
      this._uploadConfirmed = true
      this._showUploadEmptyState()
      this._uploadModal?.hide()
      return
    }

    if (this.hasUploadUrlValue) {
      await this._saveAvatarToServer(file)
      return
    }

    if (this._pendingPreviewUrl || !this.hasAvatarValue) {
      if (this.hasRemoveFlagTarget) {
        this.removeFlagTarget.value = "0"
      }

      const previewUrl = this._pendingPreviewUrl || URL.createObjectURL(file)
      this._setPreviewFromUrl(previewUrl)
      this._updateExistingAvatarMetadata(file, previewUrl)
      this._showHasAvatarPhotoState()
      this._pendingPreviewUrl = null
    }

    this._uploadConfirmed = true
    this._showUploadEmptyState()
    this._uploadModal.hide()
  }

  changeUpload(event) {
    event?.preventDefault()
    this._resetFileInput()
    this._revokePendingPreviewUrl()
    this._showUploadEmptyState()
    this.openFilePicker()
  }

  handleFileChange(event) {
    const file = event.target.files?.[0]
    if (!file) return

    this._processFile(file)
  }

  dragOver(event) {
    event.preventDefault()
  }

  dragEnter(event) {
    event.preventDefault()
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.add("is-drag-over")
    }
  }

  dragLeave(event) {
    event.preventDefault()
    if (this.hasDropzoneTarget && !this.dropzoneTarget.contains(event.relatedTarget)) {
      this.dropzoneTarget.classList.remove("is-drag-over")
    }
  }

  drop(event) {
    event.preventDefault()

    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove("is-drag-over")
    }

    const file = event.dataTransfer.files?.[0]
    if (file) this._processFile(file)
  }

  confirmCrop(event) {
    event?.preventDefault()
    if (!this._cropper) return

    const canvas = this._cropper.getCroppedCanvas({
      width: this.outputSizeValue,
      height: this.outputSizeValue,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: "high"
    })

    canvas.toBlob((blob) => {
      if (!blob) return
      const file = new File([blob], "avatar.jpg", { type: "image/jpeg" })
      const dt = new DataTransfer()
      dt.items.add(file)
      this.fileInputTarget.files = dt.files

      if (this.hasRemoveFlagTarget) {
        this.removeFlagTarget.value = "0"
      }

      this._setPreviewFromUrl(URL.createObjectURL(blob))
      this._showRemoveButton()
      this._cropModal.hide()
    }, "image/jpeg", 0.92)
  }

  cancelCrop(event) {
    event?.preventDefault()
    this._resetFileInput()
    this._cropModal.hide()
  }

  openRemoveModal(event) {
    event?.preventDefault()

    if (!this.hasAvatarValue || !this.hasRemoveModalTarget) return

    this._populateRemoveModalPreview()
    this._removeModal.show()
  }

  closeRemoveModal(event) {
    event?.preventDefault()
    this._removeModal?.hide()
  }

  async confirmRemoveAvatar(event) {
    event?.preventDefault()

    if (this.hasRemoveUrlValue) {
      await this._removeAvatarFromServer()
      return
    }

    this._removeModal?.hide()
    this._applyAvatarRemoval()
  }

  removeAvatar(event) {
    event?.preventDefault()
    this._applyAvatarRemoval()
  }

  _applyAvatarRemoval() {
    this._resetFileInput()
    this._revokePendingPreviewUrl()

    if (this.hasRemoveFlagTarget) {
      this.removeFlagTarget.value = "1"
    }

    this.hasAvatarValue = false
    this.existingAvatarUrlValue = ""
    this.existingAvatarNameValue = ""
    this.existingAvatarSizeValue = 0
    this._setPreviewToInitials()
    this._showEmptyPhotoState()
  }

  async _saveAvatarToServer(file) {
    if (this.hasConfirmBtnTarget) {
      this.confirmBtnTarget.disabled = true
    }

    const formData = new FormData()
    formData.append("user[avatar]", file)

    try {
      const response = await fetch(this.uploadUrlValue, {
        method: "PATCH",
        body: formData,
        headers: {
          "X-CSRF-Token": this._csrfToken(),
          "Accept": "application/json"
        },
        credentials: "same-origin"
      })

      const data = await response.json()

      if (response.ok && data.success) {
        this._setPreviewFromUrl(data.avatar_url)
        this.hasAvatarValue = true
        this.existingAvatarUrlValue = data.avatar_url
        this.existingAvatarNameValue = data.filename
        this.existingAvatarSizeValue = data.byte_size
        this._showHasAvatarPhotoState()
        this._resetFileInput()
        this._revokePendingPreviewUrl()
        this._uploadConfirmed = true
        this._showUploadEmptyState()
        this._uploadModal?.hide()
        this._showSnackbar("success", data.message)
      } else {
        this._showSnackbar("error", data.errors?.join(", ") || data.message)
      }
    } catch (_error) {
      this._showSnackbar("error", "Photo could not be saved.")
    } finally {
      if (this.hasConfirmBtnTarget) {
        this.confirmBtnTarget.disabled = false
      }
    }
  }

  async _removeAvatarFromServer() {
    try {
      const response = await fetch(this.removeUrlValue, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": this._csrfToken(),
          "Accept": "application/json"
        },
        credentials: "same-origin"
      })

      const data = await response.json()

      if (response.ok && data.success) {
        this._applyAvatarRemoval()
        this._removeModal?.hide()
        this._showSnackbar("success", data.message)
      } else {
        this._showSnackbar("error", data.errors?.join(", ") || data.message)
      }
    } catch (_error) {
      this._showSnackbar("error", "Photo could not be removed.")
    }
  }

  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  _showSnackbar(type, title) {
    const template = type === "error"
      ? (this.hasErrorToastTarget ? this.errorToastTarget : null)
      : (this.hasSuccessToastTarget ? this.successToastTarget : null)

    if (!template) return

    const node = template.content.firstElementChild.cloneNode(true)
    const titleEl = node.querySelector(".snackbar-title span")
    if (titleEl) titleEl.textContent = title

    this._flashContainer().appendChild(node)
  }

  _flashContainer() {
    let container = document.querySelector(".app-flash-alert-container")
    if (!container) {
      container = document.createElement("div")
      container.className = "app-flash-alert-container"
      document.body.appendChild(container)
    }
    return container
  }

  _processFile(file) {
    if (!file.type.startsWith("image/")) {
      this._resetFileInput()
      return
    }

    const dt = new DataTransfer()
    dt.items.add(file)
    this.fileInputTarget.files = dt.files

    if (this.hasUploadModalTarget) {
      this._revokePendingPreviewUrl()
      this._pendingPreviewUrl = URL.createObjectURL(file)
      this.uploadPreviewImageTarget.src = this._pendingPreviewUrl
      this.uploadPreviewMetaTarget.textContent = `${file.name} · ${this._formatFileSize(file.size)}`
      this._showUploadPreviewState()
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      this.cropperImageTarget.src = e.target.result
      this._cropModal.show()
      this.modalTarget.addEventListener("shown.bs.modal", () => {
        this._initCropper()
      }, { once: true })
    }
    reader.readAsDataURL(file)
  }

  _showUploadEmptyState() {
    if (!this.hasUploadModalContentTarget) return

    this.uploadModalContentTarget.classList.remove("is-preview")

    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.hidden = false
    }

    if (this.hasUploadPreviewTarget) {
      this.uploadPreviewTarget.hidden = true
    }

    if (this.hasUploadActionsTarget) {
      this.uploadActionsTarget.hidden = true
    }
  }

  _showUploadPreviewState() {
    if (!this.hasUploadModalContentTarget) return

    this.uploadModalContentTarget.classList.add("is-preview")

    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.hidden = true
    }

    if (this.hasUploadPreviewTarget) {
      this.uploadPreviewTarget.hidden = false
    }

    if (this.hasUploadActionsTarget) {
      this.uploadActionsTarget.hidden = false
    }
  }

  _revokePendingPreviewUrl() {
    if (this._pendingPreviewUrl) {
      URL.revokeObjectURL(this._pendingPreviewUrl)
      this._pendingPreviewUrl = null
    }
  }

  _populateRemoveModalPreview() {
    const { url, name, size } = this._avatarPreviewDetails()

    this.removePreviewImageTarget.src = url
    this.removePreviewMetaTarget.textContent = size > 0
      ? `${name} · ${this._formatFileSize(size)}`
      : name
  }

  _avatarPreviewDetails() {
    const previewImg = this.previewTarget.querySelector("img")
    const url = previewImg?.currentSrc || previewImg?.src || this.existingAvatarUrlValue
    const name = this.existingAvatarNameValue || previewImg?.alt || "avatar.jpg"
    const size = this.existingAvatarSizeValue

    return { url, name, size }
  }

  _showExistingAvatarInModal() {
    const { url, name, size } = this._avatarPreviewDetails()

    this.uploadPreviewImageTarget.src = url
    this.uploadPreviewMetaTarget.textContent = size > 0
      ? `${name} · ${this._formatFileSize(size)}`
      : name

    this._showUploadPreviewState()
  }

  _updateExistingAvatarMetadata(file, url) {
    this.hasAvatarValue = true
    this.existingAvatarUrlValue = url
    this.existingAvatarNameValue = file.name
    this.existingAvatarSizeValue = file.size
  }

  _showHasAvatarPhotoState() {
    if (!this.hasPhotoBlockTarget) {
      this._showRemoveButton()
      return
    }

    this.photoBlockTarget.classList.add("hub-account-settings-photo--has-avatar")

    const avatar = this.photoBlockTarget.querySelector(".hub-account-settings-avatar")
    if (avatar) {
      avatar.classList.remove("hub-account-settings-avatar--interactive")
      avatar.removeAttribute("data-action")
      avatar.removeAttribute("role")
      avatar.removeAttribute("tabindex")
      avatar.removeAttribute("aria-label")
    }

    if (this.hasAddPhotoBtnTarget) {
      this.addPhotoBtnTarget.hidden = true
    }

    if (this.hasExistingActionsTarget) {
      this.existingActionsTarget.hidden = false
    }
  }

  _showEmptyPhotoState() {
    if (!this.hasPhotoBlockTarget) return

    this.photoBlockTarget.classList.remove("hub-account-settings-photo--has-avatar")

    const avatar = this.photoBlockTarget.querySelector(".hub-account-settings-avatar")
    if (avatar) {
      avatar.classList.add("hub-account-settings-avatar--interactive")
      avatar.dataset.action = "click->avatar-upload#openUploadModal"
      avatar.setAttribute("role", "button")
      avatar.setAttribute("tabindex", "0")
      avatar.setAttribute("aria-label", "Add profile photo")
    }

    if (this.hasAddPhotoBtnTarget) {
      this.addPhotoBtnTarget.hidden = false
    }

    if (this.hasExistingActionsTarget) {
      this.existingActionsTarget.hidden = true
    }
  }

  _formatFileSize(bytes) {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)}KB`
    }

    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
  }

  async _initCropper() {
    // cropperjs only matters once someone picks a file to crop, so it loads on
    // demand instead of riding the entry bundle.
    const { default: Cropper } = await import("cropperjs")
    this._teardownCropper()
    this._cropper = new Cropper(this.cropperImageTarget, {
      aspectRatio: 1,
      viewMode: 1,
      autoCropArea: 1,
      background: false,
      movable: true,
      zoomable: true,
      rotatable: false,
      scalable: false,
      responsive: true,
      restore: false
    })
  }

  _teardownCropper() {
    if (this._cropper) {
      this._cropper.destroy()
      this._cropper = null
    }
  }

  _resetFileInput() {
    this.fileInputTarget.value = ""
  }

  _setPreviewFromUrl(url) {
    this.previewTarget.innerHTML = ""
    const img = document.createElement("img")
    img.src = url
    img.alt = "Avatar preview"
    img.className = this.previewTarget.dataset.imageClass || "profile-avatar-img"
    this.previewTarget.appendChild(img)
  }

  _setPreviewToInitials() {
    this.previewTarget.innerHTML = ""
    const span = document.createElement("span")
    span.className = this.previewTarget.dataset.initialsClass || "profile-avatar-initials"
    span.textContent = this.initialsValue || ""
    this.previewTarget.appendChild(span)
  }

  _showRemoveButton() {
    if (this.hasRemoveBtnTarget) {
      this.removeBtnTarget.classList.remove("d-none")
    }
  }

  _hideRemoveButton() {
    if (this.hasRemoveBtnTarget) {
      this.removeBtnTarget.classList.add("d-none")
    }
  }
}
